import React, { useState, useEffect } from 'react';
import { Steps, Card, Button, Select, Alert, Spin } from 'antd';
import { Content } from 'antd/lib/layout/layout';
import { UploadOutlined, CheckCircleOutlined, SaveOutlined } from '@ant-design/icons';
import DashboardBreadcrumb from '../../components/layout/DashboardBreadcrumb';
import ExcelUploader from '../../components/bulk-plan/ExcelUploader';
import PlanPreviewTable from '../../components/bulk-plan/PlanPreviewTable';
import { openNotificationError, openNotificationSuccess } from '../../helpers/NotificationHelpers';
import { GetPerformanceCyclesApi } from '../../api/bulk-plan/GetPerformanceCyclesApi';
import { GetPlanAttributesApi } from '../../api/bulk-plan/GetPlanAttributesApi';
import { CreateBulkPlansApi } from '../../api/bulk-plan/CreateBulkPlansApi';

const { Step } = Steps;
const { Option } = Select;

function BulkPlanCreator() {
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [performanceCycles, setPerformanceCycles] = useState([]);
  const [selectedCycle, setSelectedCycle] = useState(null);
  const [cycleLoading, setCycleLoading] = useState(false);
  const [planHierarchy, setPlanHierarchy] = useState([]);
  const [hierarchyStats, setHierarchyStats] = useState(null);
  const [selectedPlans, setSelectedPlans] = useState([]);
  const [planAttributes, setPlanAttributes] = useState([]);

  const proHeight = document.body.clientHeight;

  useEffect(() => {
    loadPerformanceCycles();
  }, []);

  async function loadPerformanceCycles() {
    setCycleLoading(true);
    try {
      console.log('Loading performance cycles...');
      const response = await GetPerformanceCyclesApi();
      console.log('Performance cycles response:', response);
      
      if (response && response.success && response.data) {
        console.log('Setting cycles:', response.data);
        setPerformanceCycles(response.data);
      } else {
        console.error('Invalid response format:', response);
        openNotificationError('bottomRight', 'Failed to load performance cycles');
      }
    } catch (error) {
      console.error('Error loading cycles:', error);
      openNotificationError('bottomRight', 'Failed to load performance cycles');
    } finally {
      setCycleLoading(false);
    }
  }

  function handleCycleChange(cycleId) {
    const cycle = performanceCycles.find(c => c.id === cycleId);
    setSelectedCycle(cycle);
    
    // Load plan attributes for this cycle
    if (cycle) {
      loadPlanAttributes(cycle.id);
    }
  }

  async function loadPlanAttributes(cycleId) {
    try {
      console.log('Loading plan attributes for cycle:', cycleId);
      const response = await GetPlanAttributesApi(cycleId);
      console.log('Plan attributes API response:', response);
      console.log('Response type:', typeof response);
      console.log('Response.data:', response?.data);
      console.log('Response.data is array?', Array.isArray(response?.data));
      
      if (response && response.success && Array.isArray(response.data)) {
        console.log('✓ Setting plan attributes array with', response.data.length, 'items');
        setPlanAttributes(response.data); // Extract the data array
      } else {
        console.error('✗ Invalid plan attributes response - not an array:', response);
        setPlanAttributes([]);
      }
    } catch (error) {
      console.error('Error loading plan attributes:', error);
      setPlanAttributes([]);
    }
  }

  function handleUploadSuccess(data) {
    setPlanHierarchy(data.hierarchy || []);
    setHierarchyStats(data.stats);
    setCurrentStep(1);
    openNotificationSuccess('bottomRight', `Successfully parsed ${data.stats?.totalPlans || 0} plans`);
  }

  function handlePlanSelectionChange(selectedPlanIds) {
    setSelectedPlans(selectedPlanIds);
  }

  function handleHierarchyUpdate(updatedHierarchy) {
    setPlanHierarchy(updatedHierarchy);
  }

  function matchCustomAttributes(customAttributes) {
    console.log('=== matchCustomAttributes START ===');
    console.log('customAttributes input:', customAttributes);
    console.log('planAttributes state:', planAttributes);
    console.log('planAttributes is array?', Array.isArray(planAttributes));
    console.log('planAttributes type:', typeof planAttributes);
    
    if (!customAttributes) {
      console.log('No customAttributes provided');
      return [];
    }
    
    if (!planAttributes) {
      console.log('planAttributes is null/undefined');
      return [];
    }
    
    if (!Array.isArray(planAttributes)) {
      console.error('ERROR: planAttributes is not an array!', planAttributes);
      return [];
    }
    
    if (planAttributes.length === 0) {
      console.log('planAttributes array is empty');
      return [];
    }

    const matched = [];

    // Iterate through custom attributes from Excel
    Object.keys(customAttributes).forEach(attrName => {
      const value = customAttributes[attrName];
      
      console.log(`\nProcessing attribute: ${attrName}, value: ${value}, type: ${typeof value}`);
      
      // Skip null/undefined values
      if (value === null || value === undefined) {
        console.log(`  Skipping ${attrName}: value is null/undefined`);
        return;
      }

      // Find matching attribute by name (case-insensitive)
      // Also try to match with number prefixes like "5. Is Active" -> "Is active"
      // IMPORTANT: Prioritize dropdown attributes, then double/number, then text
      
      // First, try to find dropdown match
      let attribute = planAttributes.find(attr => {
        const attrNameLower = attr.name.toLowerCase();
        const searchNameLower = attrName.toLowerCase();
        
        // Direct match for dropdown
        if (attrNameLower === searchNameLower && attr.valueType === 'dropdown') {
          return true;
        }
        
        // Match with number prefix removed for dropdown
        const attrNameWithoutPrefix = attrNameLower.replace(/^\d+\.\s*/, '');
        if (attrNameWithoutPrefix === searchNameLower && attr.valueType === 'dropdown') {
          return true;
        }
        
        return false;
      });
      
      // If no dropdown match, try double/number types
      if (!attribute) {
        attribute = planAttributes.find(attr => {
          const attrNameLower = attr.name.toLowerCase();
          const searchNameLower = attrName.toLowerCase();
          
          // Direct match for double/number
          if (attrNameLower === searchNameLower && (attr.valueType === 'double' || attr.valueType === 'number')) {
            return true;
          }
          
          // Match with number prefix removed for double/number
          const attrNameWithoutPrefix = attrNameLower.replace(/^\d+\.\s*/, '');
          if (attrNameWithoutPrefix === searchNameLower && (attr.valueType === 'double' || attr.valueType === 'number')) {
            return true;
          }
          
          return false;
        });
      }
      
      // If still no match, try any other type (text, date, etc.)
      if (!attribute) {
        attribute = planAttributes.find(attr => {
          const attrNameLower = attr.name.toLowerCase();
          const searchNameLower = attrName.toLowerCase();
          
          // Direct match
          if (attrNameLower === searchNameLower) {
            return true;
          }
          
          // Try removing number prefix (e.g., "5. Is Active" -> "is active")
          const attrNameWithoutPrefix = attrNameLower.replace(/^\d+\.\s*/, '');
          if (attrNameWithoutPrefix === searchNameLower) {
            return true;
          }
          
          return false;
        });
      }

      if (!attribute) {
        console.warn(`  Attribute not found in planAttributes: ${attrName}`);
        console.warn(`  Available attributes:`, planAttributes.map(a => a.name));
        return;
      }

      console.log(`  Found attribute: ${attribute.name}, type: ${attribute.valueType}, id: ${attribute.id}`);

      // For dropdown attributes, match the value to dropdown_attribute_value ID
      if (attribute.valueType === 'dropdown') {
        if (!attribute.dropdownValues) {
          console.warn(`  No dropdownValues for attribute: ${attrName}`);
          return;
        }
        
        console.log(`  Dropdown values available:`, attribute.dropdownValues.map(dv => dv.value));
        
        // Handle multiple values separated by comma (e.g., "Kushlani, IA")
        const valueStr = String(value).trim();
        const multipleValues = valueStr.split(',').map(v => v.trim()).filter(v => v);
        
        if (multipleValues.length > 1) {
          console.log(`  Multiple dropdown values detected:`, multipleValues);
          // Match each value and collect all IDs
          const matchedIds = [];
          multipleValues.forEach(val => {
            const dropdownValue = attribute.dropdownValues.find(
              dv => dv.value.toLowerCase() === val.toLowerCase()
            );
            if (dropdownValue) {
              console.log(`    ✓ Matched: ${dropdownValue.value} (ID: ${dropdownValue.id})`);
              matchedIds.push(dropdownValue.id);
            } else {
              console.warn(`    ✗ Not found: ${val}`);
            }
          });
          
          if (matchedIds.length > 0) {
            // Store as comma-separated IDs
            matched.push({
              attributeId: attribute.id,
              value: matchedIds.join(','),
            });
          }
        } else {
          // Single value
          const dropdownValue = attribute.dropdownValues.find(
            dv => dv.value.toLowerCase() === valueStr.toLowerCase()
          );

          if (dropdownValue) {
            console.log(`  ✓ Matched dropdown value: ${dropdownValue.value} (ID: ${dropdownValue.id})`);
            matched.push({
              attributeId: attribute.id,
              value: dropdownValue.id,
            });
          } else {
            console.warn(`  ✗ Dropdown value not found: ${value} for attribute ${attrName}`);
          }
        }
      } else if (attribute.valueType === 'number') {
        // For number type, convert string to number
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        if (!isNaN(numValue)) {
          console.log(`  ✓ Using number value: ${numValue}`);
          matched.push({
            attributeId: attribute.id,
            value: numValue,
          });
        } else {
          console.warn(`  ✗ Invalid number value: ${value}`);
        }
      } else if (attribute.valueType === 'boolean') {
        // For boolean type, convert to boolean
        const boolValue = value === '1' || value === 1 || value === true || value === 'true' || value === 'yes';
        console.log(`  ✓ Using boolean value: ${boolValue}`);
        matched.push({
          attributeId: attribute.id,
          value: boolValue ? 1 : 0, // Store as 1 or 0
        });
      } else {
        // For other types (text, double, date), use the value directly
        console.log(`  ✓ Using direct value for ${attribute.valueType}: ${value}`);
        matched.push({
          attributeId: attribute.id,
          value: value,
        });
      }
    });

    console.log('\n=== matchCustomAttributes END ===');
    console.log('Total matched attributes:', matched.length);
    console.log('Matched attributes:', matched);
    return matched;
  }

  function handleBack() {
    setCurrentStep(currentStep - 1);
  }

  async function handleCreatePlans() {
    if (selectedPlans.length === 0) {
      openNotificationError('bottomRight', 'Please select at least one plan to create');
      return;
    }

    setLoading(true);
    try {
      const allPlans = flattenHierarchy(planHierarchy);
      const plansToCreate = allPlans
        .filter(plan => selectedPlans.includes(plan.id))
        .map(plan => {
          console.log(`\n=== Preparing plan: ${plan.title} ===`);
          console.log('Plan customAttributes:', plan.customAttributes);
          
          const matchedAttributes = matchCustomAttributes(plan.customAttributes);
          console.log('Matched attributes for this plan:', matchedAttributes);
          
          return {
            id: plan.id,
            type: plan.type, // Keep the original type (TOP_PLAN, CASCADE_PLAN, or SUB_CASCADE_PLAN)
            title: plan.title,
            description: plan.description,
            unitId: plan.unit,
            assignedTo: plan.assignedTo,
            target: plan.target,
            targetDate: plan.targetDate,
            scaleId: plan.scaleId,
            scaleUnitId: plan.scaleUnitId,
            planGroupId: plan.planGroup,
            performanceCycleId: selectedCycle.id,
            parentPlanId: plan.parentId,
            hierarchyOrder: plan.hierarchyOrder,
            customAttributes: matchedAttributes,
            canExceed: false,
          };
        });

      console.log('\n=== SENDING TO BACKEND ===');
      console.log('Total plans to create:', plansToCreate.length);
      console.log('Plans with custom attributes:', plansToCreate.filter(p => p.customAttributes && p.customAttributes.length > 0).length);
      console.log('Full payload:', JSON.stringify(plansToCreate, null, 2));

      const response = await CreateBulkPlansApi(plansToCreate);
      
      if (response.error) {
        openNotificationError('bottomRight', response.message);
      } else {
        openNotificationSuccess(
          'bottomRight',
          `Successfully created ${response.successCount} of ${response.totalPlans} plans`
        );
        setCurrentStep(2);
      }
    } catch (error) {
      console.error('Error creating plans:', error);
      openNotificationError('bottomRight', 'Failed to create plans');
    } finally {
      setLoading(false);
    }
  }

  function flattenHierarchy(plans) {
    const result = [];
    const flatten = (planList) => {
      planList.forEach(plan => {
        result.push(plan);
        if (plan.children && plan.children.length > 0) {
          flatten(plan.children);
        }
      });
    };
    flatten(plans);
    return result;
  }

  function handleReset() {
    setCurrentStep(0);
    setPlanHierarchy([]);
    setHierarchyStats(null);
    setSelectedPlans([]);
    setSelectedCycle(null);
  }

  function renderStepContent() {
    switch (currentStep) {
      case 0:
        return (
          <Card title="Upload Excel File" style={{ marginTop: 24 }}>
            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                Select Performance Cycle *
              </label>
              <Select
                style={{ width: '100%', maxWidth: 400 }}
                placeholder="Select a performance cycle"
                value={selectedCycle?.id}
                onChange={handleCycleChange}
                loading={cycleLoading}
                disabled={cycleLoading}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {performanceCycles.map(cycle => (
                  <Option key={cycle.id} value={cycle.id}>
                    {cycle.name}
                  </Option>
                ))}
              </Select>
            </div>

            {!selectedCycle && (
              <Alert
                message="Performance cycle required"
                description="Please select a performance cycle before uploading the Excel file"
                type="warning"
                showIcon
                style={{ marginBottom: 24 }}
              />
            )}

            <ExcelUploader
              performanceCycleId={selectedCycle?.id}
              onSuccess={handleUploadSuccess}
              disabled={!selectedCycle}
            />
          </Card>
        );

      case 1:
        return (
          <Card title="Review and Edit Plans" style={{ marginTop: 24 }}>
            {hierarchyStats && (
              <Alert
                message={`Detected ${hierarchyStats.totalPlans} plans`}
                description={`${hierarchyStats.topPlans} Top Plans, ${hierarchyStats.cascadePlans} Cascade Plans, ${hierarchyStats.subCascadePlans} Sub-Cascade Plans`}
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            <PlanPreviewTable
              hierarchy={planHierarchy}
              selectedPlans={selectedPlans}
              onSelectionChange={handlePlanSelectionChange}
              onHierarchyUpdate={handleHierarchyUpdate}
              performanceCycleId={selectedCycle?.id}
            />

            <div style={{ marginTop: 24, display: 'flex', justifyContent: 'space-between' }}>
              <Button onClick={handleBack}>
                Back
              </Button>
              <Button
                type="primary"
                onClick={handleCreatePlans}
                loading={loading}
                disabled={selectedPlans.length === 0}
                icon={<SaveOutlined />}
              >
                Create {selectedPlans.length} Plan{selectedPlans.length !== 1 ? 's' : ''}
              </Button>
            </div>
          </Card>
        );

      case 2:
        return (
          <Card title="Plans Created Successfully" style={{ marginTop: 24 }}>
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <CheckCircleOutlined style={{ fontSize: 64, color: '#52c41a', marginBottom: 24 }} />
              <h2>Successfully created {selectedPlans.length} plans!</h2>
              <p style={{ color: '#666', marginBottom: 32 }}>
                All selected plans have been created in the system.
              </p>
              <Button type="primary" onClick={handleReset}>
                Create More Plans
              </Button>
            </div>
          </Card>
        );

      default:
        return null;
    }
  }

  return (
    <Content style={{ padding: '0 24px', minHeight: proHeight - 64, maxHeight: proHeight - 64, overflow: 'auto' }}>
      <DashboardBreadcrumb
        items={[
          { title: 'Settings', path: '/settings' },
          { title: 'Bulk Plan Creator' },
        ]}
      />

      <div style={{ background: '#fff', padding: 24, minHeight: proHeight - 150 }}>
        <h1>Bulk Plan Creator</h1>
        <p style={{ color: '#666', marginBottom: 24 }}>
          Upload an Excel file to create multiple plans at once with automatic hierarchy detection
        </p>

        <Steps current={currentStep} style={{ marginBottom: 24 }}>
          <Step title="Upload" icon={<UploadOutlined />} />
          <Step title="Review & Edit" />
          <Step title="Complete" icon={<CheckCircleOutlined />} />
        </Steps>

        {renderStepContent()}
      </div>
    </Content>
  );
}

export default BulkPlanCreator;
